import { Box, Button, Container, Grid, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

const StreamLine = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        py: { xs: 8, md: 6 },
        background:
          "linear-gradient(180deg, #ffffff 0%, #f8fbff 40%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="lg">
        {/* INTRO SECTION */}
        <Box textAlign="center" mb={{ xs: 8, md: 6 }}>
          <Typography
            variant="h2"
            fontWeight={700}
            sx={{ letterSpacing: "-0.5px", lineHeight: 1.15 }}
            gutterBottom
          >
            Streamline Your Workflow with{" "}
            <Box component="span" color="primary.main">
              MySchoolling
            </Box>
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 820,
              mx: "auto",
              mt: 3,
              lineHeight: 1.8,
            }}
          >
            An AI-powered digital ecosystem designed to automate school
            operations, strengthen parent engagement, and empower students with
            intelligent learning tools — built for modern education.
          </Typography>
        </Box>

        {/* WHY CHOOSE HEADER */}
        <Box id="Why_Choose" textAlign="center" mb={{ xs: 6, md: 6 }}>
          <Typography variant="h3" fontWeight={800}>
            Why Choose MySchoolling?
          </Typography>
        </Box>

        {/* FEATURE 1 */}
        <Grid container spacing={{ xs: 6, md: 6 }} alignItems="center" mb={10}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              Intelligent School Operations
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.9, fontSize: "1.05rem" }}
            >
              Automate attendance, fee management, timetables, reporting,
              and academic workflows with precision and zero friction.
              <br /><br />
              Real-time dashboards and smart notifications provide full
              operational visibility, enabling faster decisions and improved
              administrative efficiency.
              <br /><br />
              Reduce manual work, eliminate errors, and allow educators to
              focus on what truly matters — delivering quality education.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/feature1.png"
              alt="Smart Administration"
              sx={{
                width: "100%",
                maxWidth: 520,
                borderRadius: 4,
                boxShadow: "0 40px 100px rgba(25,118,210,0.15)",
              }}
            />
          </Grid>
        </Grid>

        {/* FEATURE 2 */}
        <Grid
          container
          spacing={{ xs: 6, md: 8 }}
          alignItems="center"
          mb={10}
          flexDirection={{ xs: "column", md: "row-reverse" }}
        >
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              AI That Learns With Students
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.9, fontSize: "1.05rem" }}
            >
              Provide students with personalized, 24/7 AI-driven academic
              support tailored to their strengths, pace, and learning style.
              <br /><br />
              Intelligent insights identify weak areas, reinforce concepts,
              and deliver structured explanations that enhance clarity.
              <br /><br />
              Empower learners with confidence, independence, and measurable
              academic growth powered by smart technology.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/feature2.png"
              alt="AI Tutor"
              sx={{
                width: "100%",
                maxWidth: 520,
                borderRadius: 4,
                boxShadow: "0 40px 100px rgba(25,118,210,0.15)",
              }}
            />
          </Grid>
        </Grid>

        {/* FEATURE 3 */}
        <Grid container spacing={{ xs: 6, md: 6 }} alignItems="center" mb={10}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Gamified Engagement Engine
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.9, fontSize: "1.05rem" }}
            >
              Transform assessments into interactive challenges using
              skill-based quizzes, achievements, and reward systems.
              <br /><br />
              Real-time performance analytics help teachers track learning
              gaps while keeping students motivated and engaged.
              <br /><br />
              Make learning dynamic, measurable, and enjoyable —
              built for today’s digital classrooms.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/feature3.png"
              alt="Gamified Learning"
              sx={{
                width: "100%",
                maxWidth: 520,
                borderRadius: 4,
                boxShadow: "0 40px 100px rgba(25,118,210,0.15)",
              }}
            />
          </Grid>
        </Grid>

        {/* CTA */}
        <Box
          textAlign="center"
          mt={{ xs: 10, md: 12 }}
          sx={{
            p: { xs: 6, md: 8 },
            borderRadius: 4,
            background:
              "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "#ffffff",
          }}
        >
          <Typography variant="h3" fontWeight={800} gutterBottom>
            Ready to Transform Your School?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              maxWidth: 700,
              mx: "auto",
              opacity: 0.9,
              lineHeight: 1.8,
              mb: 4,
            }}
          >
            Join forward-thinking institutions already leveraging AI-powered
            automation and intelligent learning tools to stay ahead.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/contact")}
            sx={{
              backgroundColor: "#ffffff",
              color: "#1976d2",
              fontWeight: 700,
              textTransform: "none",
              px: 5,
              py: 1.8,
              borderRadius: 3,
              fontSize: "1rem",
              boxShadow: "0 15px 40px rgba(0,0,0,0.2)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#f0f6ff",
                transform: "translateY(-3px)",
                boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              },
            }}
          >
            Request a Demo
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default StreamLine;